import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  initializeSignalRConnection,
  disconnectSignalR,
} from "../services/signalR";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { addMessage, updateMessageStatus } from "../redux/messagesSlice";
import { selectContact, updateContactStatus } from "../redux/contactSlice";
import { base64ToUint8Array, decryptMessage } from "../services/crypto";
import { parseMessage } from "../utils/messageUtils";

import { combineAndDecryptPrivateKey, decryptString } from "../utils/security";
import { HubConnection } from "@microsoft/signalr";
import {
  incrementUnreadCount,
  updateContactLastMessage,
} from "../redux/chatListSlice";

type MessageHandler = (encrypted: any, sender: string) => Promise<void>;
type StatusHandler = (
  userId: string,
  isOnline: boolean,
  lastSeen: string
) => void;

interface ConnectionContextType {
  connection: any;
  isConnected: boolean;
  isConnecting: boolean;
  status: "disconnected" | "connecting" | "connected";
  startConnection: (
    token: string,
    messageHandler: MessageHandler,
    statusHandler: StatusHandler
  ) => Promise<void>;
  stopConnection: () => Promise<void>;
  //   initializeConnection: (
  //     token: string,
  //     onMessageReceived: MessageHandler,
  //     onUserStatusChanged: StatusHandler
  //   ) => Promise<void>;
  disconnect: () => Promise<void>;
}
let currentConnection: HubConnection | null = null;

const ConnectionContext = createContext<ConnectionContextType>({
  connection: null,
  isConnected: false,
  isConnecting: false,
  status: "disconnected",
  //   initializeConnection: async () => {},
  disconnect: async () => {},
  startConnection: async () => {},
  stopConnection: async () => {},
});

// contexts/ConnectionContext.tsx
export const ConnectionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [status, setStatus] = useState<
    "disconnected" | "connecting" | "connected"
  >("disconnected");
  const abortControllerRef = useRef<AbortController | null>(null);
  const dispatch = useAppDispatch();

  const startConnection = useCallback(
    async (
      token: string,
      messageHandler: MessageHandler,
      statusHandler: StatusHandler
    ) => {
      if (status === "connecting" || status === "connected") return;

      setStatus("connecting");
      abortControllerRef.current = new AbortController();

      try {
        // Clear any existing connection first
        // if (connection && connection.state !== "Disconnected") {
        //   console.log("Stopping existing connection...");
        //   await connection.stop();
        // }

        const newConnection = await initializeSignalRConnection(
          token,
          abortControllerRef.current.signal
        );

        // Setup handlers
        newConnection.on("ReceiveEncryptedMessage", messageHandler);
        newConnection.on("UserStatusChanged", statusHandler);
        newConnection.on("MessageRead", (messageId: string) => {
          dispatch(updateMessageStatus({ id: messageId, status: "read" }));
        });
        newConnection.on("MessageAck", (messageId: string) => {
          dispatch(updateMessageStatus({ id: messageId, status: "sent" }));
        });
        newConnection.onclose(() => {
          setStatus("disconnected");
          setTimeout(
            () => startConnection(token, messageHandler, statusHandler),
            5000
          );
        });

        setConnection(newConnection);
        setCurrentConnection(newConnection);

        setStatus("connected");
      } catch (error) {
        setStatus("disconnected");
        if (
          typeof error === "object" &&
          error !== null &&
          "name" in error &&
          (error as any).name !== "AbortError"
        ) {
          console.error("Connection error:", error);
          // Retry with exponential backoff
          setTimeout(
            () => startConnection(token, messageHandler, statusHandler),
            5000
          );
        }
      }
    },
    [connection, status]
  );

  const stopConnection = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (connection) {
      try {
        await connection.stop();
      } catch (error) {
        if (
          typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof (error as any).message === "string" &&
          !(error as any).message.includes("already in the disconnected state")
        ) {
          console.error("Disconnection error:", error);
        }
      } finally {
        setConnection(null);
        setStatus("disconnected");
      }
    } else {
      setStatus("disconnected");
    }
  }, [connection]);

  //   const disconnect = useCallback(async () => {
  //     await stopConnection();
  //     setStatus("disconnected");
  //   }, [stopConnection]);

  useEffect(() => {
    return () => {
      stopConnection();
    };
  }, [stopConnection]);

  return (
    <ConnectionContext.Provider
      value={{
        connection,
        isConnected: status === "connected",
        isConnecting: status === "connecting",
        status,
        startConnection,
        stopConnection,
        disconnect: stopConnection,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
};

export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error("useConnection must be used within a ConnectionProvider");
  }
  return context;
};

export const setCurrentConnection = (connection: HubConnection | null) => {
  currentConnection = connection;
};

const getCurrentConnection = () => {
  return currentConnection;
};

export const disconnectConnection = async () => {
  // This is a fallback for non-React contexts

  try {
    const currentConnection = getCurrentConnection();
    if (!currentConnection) {
      console.warn("No current connection to disconnect");
      return; // No connection to disconnect
    }
    console.log("Disconnecting current connection...");
    await disconnectSignalR(currentConnection); // Make sure this is properly implemented in your signalR service
  } catch (error) {
    console.error("Failed to disconnect:", error);
  }
};

// Helper hook for components that need message handling
export const useChatConnection = () => {
  const dispatch = useAppDispatch();
  const { a, b, c } = useAppSelector((state) => state.auth);
  const { selectedContact } = useAppSelector((state) => state.chatList);

  // Initialize state with values from sessionStorage immediately
  const [keys, setKeys] = useState(() => {
    const publicKey = sessionStorage.getItem("c") || c;
    const partA = sessionStorage.getItem("a") || a;
    const partB = sessionStorage.getItem("b") || b;
    let keysLoaded = false;

    let decryptedValue = null;
    try {
      decryptedValue = combineAndDecryptPrivateKey([
        decryptString(partA || ""),
        decryptString(partB || ""),
      ]);
      keysLoaded = true;
    } catch (error) {
      decryptedValue = "";
    }

    return {
      publicKey,
      privateKey: decryptedValue || null,
      keysLoaded,
    };
  });
  const messageQueue = useRef<Array<{ encrypted: any; sender: string }>>([]);
  const isProcessing = useRef(false);

  // Load private key synchronously in useEffect
  useEffect(() => {
    const loadKeys = async () => {
      const partA = sessionStorage.getItem("a") || a;
      const partB = sessionStorage.getItem("b") || b;

      if (!partA || !partB) {
        setKeys((prev) => ({ ...prev, keysLoaded: true }));
        return;
      }

      try {
        const decryptedValue = combineAndDecryptPrivateKey([
          decryptString(partA),
          decryptString(partB),
        ]);

        setKeys({
          publicKey: sessionStorage.getItem("c") || c,
          privateKey: decryptedValue,
          keysLoaded: true,
        });

        // Process any queued messages
        if (messageQueue.current.length > 0 && !isProcessing.current) {
          isProcessing.current = true;
          while (messageQueue.current.length > 0) {
            const msg = messageQueue.current.shift();
            if (msg) {
              await processMessage(msg.encrypted, msg.sender);
            }
          }
          isProcessing.current = false;
        }
      } catch (error) {
        console.error("Private key decryption failed:", error);
        setKeys((prev) => ({ ...prev, keysLoaded: true }));
      }
    };

    loadKeys();
  }, []);

  const processMessage = async (encrypted: any, sender: string) => {
    if (!keys.publicKey || !keys.privateKey) {
      throw new Error("Keys not available");
    }

    const decrypted = await decryptMessage(
      base64ToUint8Array(encrypted.senderPublicKey),
      base64ToUint8Array(keys.privateKey),
      { ciphertext: encrypted.encryptedContent, nonce: encrypted.nonce }
    );
    console.log(
      "🔓 Message Details",
      encrypted,
      "selected contact",
      selectedContact
    );

    if (selectedContact && selectedContact.id === encrypted.senderId) {
      dispatch(
        addMessage({
          id: encrypted.id,
          content: decrypted,
          timestamp: encrypted.sentAt,
          direction: "incoming",
          status: "delivered",
          sender: sender,
          parts: parseMessage(decrypted),
        })
      );
    }
    if (selectedContact && selectedContact.id !== encrypted.senderId) {
      //update the contact with the new message

      dispatch(
        updateContactLastMessage({
          contactId: `${encrypted.senderId}`,
          lastMessage: decrypted,
          lastSender: `${encrypted.senderId}`,
        })
      );
      dispatch(incrementUnreadCount({ contactId: `${encrypted.senderId}` }));
    }
  };

  const handleIncomingMessage = useCallback(
    async (encrypted: any, sender: string) => {
      // console.log("a , b and c", a, b, c);

      //console.log("🔒 Incoming message");
      // console.log(
      //   "🔑 Keys loaded",
      //   keys.keysLoaded,
      //   keys.publicKey,
      //   keys.privateKey
      // );
      if (!keys.keysLoaded) {
        // Queue the message if keys aren't loaded yet
        messageQueue.current.push({ encrypted, sender });
        return;
      }

      if (!keys.publicKey || !keys.privateKey) {
        console.error("Missing required keys - cannot decrypt");
        return;
      }

      try {
        await processMessage(encrypted, sender);
      } catch (error) {
        console.error("Message decryption failed:", error);
      }
    },
    [keys, dispatch]
  );

  const handleUserStatusChanged = useCallback(
    (userId: string, isOnline: boolean, lastSeen: string) => {
      dispatch(
        updateContactStatus({
          userId,
          status: isOnline ? "online" : "offline",
          lastSeen,
        })
      );
    },
    [dispatch]
  );

  return { handleIncomingMessage, handleUserStatusChanged };
};
